import { deckCountsLine, type DeckCounts } from './deckCountsLine.ts';
import type { ReactNode } from 'react';

/**
 * The top of a listing page: the page's name, and one muted line saying what
 * the studio holds.
 *
 * The counts are the WHOLE collection's, filtered or not — see
 * `deckCountsLine` for why the filtered number lives on the chip row instead.
 *
 * Generalised in Run 9 so the Library's top row is this one rather than a
 * second copy of it. Three additive props, all defaulted so the decks page's
 * call and its rendered markup are unchanged: `title` names the page, `line`
 * supplies a sentence the caller has already built (the Library's counts are
 * items, slides and series, not published and drafts), and `actions` puts
 * buttons on the right. Without `actions` the row is the same single column it
 * has always been; the two-column arrangement is a modifier rather than a
 * wrapper the decks page would have to grow for nothing.
 */

type Props = {
  /** Required only when `line` is absent. */
  counts?: DeckCounts;
  title?: string;
  /** Overrides the counts line. An empty string renders an empty line, which
   *  is what keeps the row's height stable while a count is still loading. */
  line?: string;
  actions?: ReactNode;
};

export default function DeckTitleRow({ counts, title = 'Decks', line, actions }: Props) {
  const text = (
    <>
      {/* The page's own h1. The app's name is in the header's brand slot, which
          is not a heading anywhere, so nothing above this competes for the
          level. */}
      <h1 className="deck-title-row__title">{title}</h1>
      <p className="deck-title-row__counts">
        {line ?? deckCountsLine(counts ?? { total: 0, published: 0, drafts: 0 })}
      </p>
    </>
  );
  if (!actions) return <div className="deck-title-row">{text}</div>;
  return (
    <div className="deck-title-row deck-title-row--actions">
      <div className="deck-title-row__text">{text}</div>
      <div className="deck-title-row__buttons">{actions}</div>
    </div>
  );
}
